import { Checkbox, Modal, notification, Spin } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCheckCircle,
  MdOutlineCloudUpload,
  MdOutlineDownload,
  MdOutlineError,
  MdOutlineInsertDriveFile,
  MdOutlineRefresh,
} from "react-icons/md";
import Button from "shared/components/Button";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { IMPORT_BRANDS_FROM_EXCEL } from "utils/api/graphql/mutations/brand";
import { BRANDS } from "utils/api/graphql/queries/brands";
import { INITIAL_VALUES } from "utils/constants";
import * as XLSX from "xlsx";

const ACCEPTED_EXTENSIONS = [".xlsx", ".csv"];
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const REQUIRED_COLUMNS = ["MARQUE", "MODELE"];
const PREVIEW_ROW_COUNT = 5;

const STEPS = {
  UPLOAD: "upload",
  PREVIEW: "preview",
  IMPORTING: "importing",
  RESULT: "result",
};

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
        resolve({ rows, headers });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function generateTemplate() {
  const workbook = XLSX.utils.book_new();
  const headers = [["MARQUE", "MODELE", "TYPE", "ANNEE"]];
  const worksheet = XLSX.utils.aoa_to_sheet(headers);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Terminaux");
  XLSX.writeFile(workbook, "modele_import_terminaux.xlsx");
}

function ImportModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BRAND);
  const IMPORT = t("IMPORT", { returnObjects: true });

  const [step, setStep] = useState(STEPS.UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const inputRef = useRef(null);

  const [executeImport] = useMutation(IMPORT_BRANDS_FROM_EXCEL, {
    refetchQueries: [
      {
        query: BRANDS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });

  const reset = () => {
    setStep(STEPS.UPLOAD);
    setSelectedFile(null);
    setParsedData(null);
    setDragOver(false);
    setFileError(null);
    setUpdateExisting(false);
    setImportResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file) => {
    setFileError(null);

    const ext = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setFileError(IMPORT.UPLOAD.INVALID_FORMAT);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError(
        IMPORT.UPLOAD.FILE_TOO_LARGE.replace(
          "{{size}}",
          MAX_FILE_SIZE_MB,
        ),
      );
      return;
    }

    try {
      const { rows, headers } = await parseFile(file);
      const missingColumns = REQUIRED_COLUMNS.filter(
        (col) => !headers.includes(col),
      );

      setSelectedFile(file);
      setParsedData({ rows, headers, missingColumns });
      setStep(STEPS.PREVIEW);
    } catch (_err) {
      setFileError(IMPORT.ERRORS.UPLOAD_FAILED);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile || !parsedData) return;

    setStep(STEPS.IMPORTING);
    try {
      const base64 = await fileToBase64(selectedFile);
      const { data } = await executeImport({
        variables: {
          data: {
            base64,
            filename: selectedFile.name,
            updateExisting,
          },
        },
      });
      const result = get(data, "importBrandsFromExcel");
      setImportResult(result);
      setStep(STEPS.RESULT);
      if (result?.success) {
        onSuccess?.();
      }
    } catch (error) {
      notification.error({
        message: IMPORT.ERRORS.IMPORT_FAILED,
        description: error.message,
      });
      setStep(STEPS.PREVIEW);
    }
  };

  const renderUpload = () => (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={classNames("brand-import__dropzone", {
          "brand-import__dropzone--dragover": dragOver,
        })}
      >
        <MdOutlineCloudUpload size={48} color={COLORS.C_GRAY} />
        <p className="brand-import__dropzone-text">
          {IMPORT.UPLOAD.DROP_TEXT}
        </p>
        <p className="brand-import__dropzone-hint">
          {IMPORT.UPLOAD.FORMATS}
        </p>
        <p className="brand-import__dropzone-hint">
          {IMPORT.UPLOAD.MAX_SIZE.replace("{{size}}", MAX_FILE_SIZE_MB)}
        </p>
      </div>
      {fileError && (
        <div className="brand-import__error">
          <MdOutlineError size={18} />
          {fileError}
        </div>
      )}
      <div
        className="brand-import__template-link"
        onClick={generateTemplate}
      >
        <MdOutlineDownload size={16} />
        {IMPORT.PREVIEW.DOWNLOAD_TEMPLATE}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.csv"
        className="hidden-input"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) handleFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );

  const renderPreview = () => {
    if (!parsedData) return null;
    const { rows, headers, missingColumns } = parsedData;
    const previewRows = rows.slice(0, PREVIEW_ROW_COUNT);
    const hasErrors = missingColumns.length > 0;

    return (
      <div>
        <div className="brand-import__preview-header">
          <div className="d-flex align--center gap-12">
            <MdOutlineInsertDriveFile
              size={20}
              color={COLORS.C_SUCCESS}
            />
            <span className="brand-import__dropzone-filename">
              {selectedFile?.name}
            </span>
            <span className="brand-import__dropzone-filesize">
              ({(selectedFile?.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <span className="brand-import__row-count">
            {IMPORT.PREVIEW.ROWS_FOUND.replace(
              "{{count}}",
              rows.length,
            )}
          </span>
        </div>

        {hasErrors && (
          <div className="brand-import__error">
            <MdOutlineError size={18} />
            {IMPORT.PREVIEW.MISSING_COLUMNS.replace(
              "{{columns}}",
              missingColumns.join(", "),
            )}
          </div>
        )}

        <div className="mt-16">
          <h4>{IMPORT.PREVIEW.FIRST_ROWS}</h4>
          <div className="mt-8 brand-import__table-wrapper">
            <table className="brand-import__preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  {headers.map((header) => (
                    <th key={header}>
                      <span
                        className={
                          REQUIRED_COLUMNS.includes(header)
                            ? missingColumns.includes(header)
                              ? "brand-import__column-missing"
                              : "brand-import__column-valid"
                            : ""
                        }
                      >
                        {header}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td>{rowIndex + 1}</td>
                    {headers.map((header) => (
                      <td key={header}>{row[header] || "-"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {!hasErrors && (
          <div className="brand-import__options">
            <div className="brand-import__option-row">
              <Checkbox
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
              />
              <span>{IMPORT.PREVIEW.UPDATE_EXISTING}</span>
            </div>
            <p className="brand-import__option-hint">
              {IMPORT.PREVIEW.UPDATE_EXISTING_HINT}
            </p>
          </div>
        )}

        <div
          className="brand-import__template-link"
          onClick={generateTemplate}
        >
          <MdOutlineDownload size={16} />
          {IMPORT.PREVIEW.DOWNLOAD_TEMPLATE}
        </div>
      </div>
    );
  };

  const renderImporting = () => (
    <div className="brand-import__loading">
      <Spin size="large" />
      <p className="brand-import__loading-text">
        {IMPORT.ACTIONS.IMPORTING}
      </p>
    </div>
  );

  const renderResult = () => {
    if (!importResult) return null;

    const success = get(importResult, "success", false);
    const errors = get(importResult, "errors", []);
    const brandsCreated = get(importResult, "brandsCreated", 0);
    const modelsCreated = get(importResult, "modelsCreated", 0);
    const modelsUpdated = get(importResult, "modelsUpdated", 0);
    const modelsSkipped = get(importResult, "modelsSkipped", 0);
    const totalRows = get(importResult, "totalRows", 0);

    return (
      <div className="brand-import__result-container">
        <div
          className={classNames("brand-import__result-icon", {
            "brand-import__result-icon--success": success,
            "brand-import__result-icon--failure": !success,
          })}
        >
          {success ? (
            <MdOutlineCheckCircle size={40} color={COLORS.C_SUCCESS} />
          ) : (
            <MdOutlineError size={40} color={COLORS.C_DANGER} />
          )}
        </div>
        <h3>
          {success
            ? IMPORT.RESULT.SUCCESS_TITLE
            : IMPORT.RESULT.PARTIAL_TITLE}
        </h3>

        <div className="brand-import__result-stats">
          <div className="brand-import__result-stat brand-import__result-stat--brands-created">
            <div className="brand-import__stat-value brand-import__stat-value--brands-created">
              {brandsCreated}
            </div>
            <div className="brand-import__stat-label">
              {IMPORT.RESULT.BRANDS_CREATED}
            </div>
          </div>
          <div className="brand-import__result-stat brand-import__result-stat--models-created">
            <div className="brand-import__stat-value brand-import__stat-value--models-created">
              {modelsCreated}
            </div>
            <div className="brand-import__stat-label">
              {IMPORT.RESULT.MODELS_CREATED}
            </div>
          </div>
          <div className="brand-import__result-stat brand-import__result-stat--models-updated">
            <div className="brand-import__stat-value brand-import__stat-value--models-updated">
              {modelsUpdated}
            </div>
            <div className="brand-import__stat-label">
              {IMPORT.RESULT.MODELS_UPDATED}
            </div>
          </div>
          <div className="brand-import__result-stat brand-import__result-stat--models-skipped">
            <div className="brand-import__stat-value brand-import__stat-value--models-skipped">
              {modelsSkipped}
            </div>
            <div className="brand-import__stat-label">
              {IMPORT.RESULT.MODELS_SKIPPED}
            </div>
          </div>
        </div>

        <div className="brand-import__result-footer">
          {totalRows} {IMPORT.RESULT.ROWS_PROCESSED}
        </div>

        {!success && errors.length > 0 && (
          <div className="brand-import__error-section">
            <h4 className="brand-import__error-title">
              {IMPORT.RESULT.ERROR_DETAILS}
            </h4>
            <div className="brand-import__error-list">
              {errors.map((err, i) => (
                <div key={i} className="brand-import__error-item">
                  <MdOutlineError size={14} />
                  Ligne {err.row}: {err.message}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getFooter = () => {
    if (step === STEPS.UPLOAD) return null;

    if (step === STEPS.IMPORTING) return null;

    if (step === STEPS.PREVIEW) {
      const hasErrors = parsedData?.missingColumns?.length > 0;
      return (
        <div className="d-flex justify--between w-100">
          <Button
            type="primary--outlined"
            onClick={() => {
              setStep(STEPS.UPLOAD);
              setSelectedFile(null);
              setParsedData(null);
              setFileError(null);
            }}
          >
            {IMPORT.ACTIONS.BACK}
          </Button>
          <div className="d-flex gap-8">
            <Button type="primary--outlined" onClick={handleClose}>
              {IMPORT.ACTIONS.CANCEL}
            </Button>
            <Button
              type="primary"
              onClick={handleImport}
              disabled={hasErrors}
            >
              {IMPORT.ACTIONS.IMPORT}
            </Button>
          </div>
        </div>
      );
    }

    if (step === STEPS.RESULT) {
      return (
        <div className="d-flex justify--between w-100">
          <Button type="primary--outlined" onClick={reset}>
            <MdOutlineRefresh size={18} />
            {IMPORT.RESULT.IMPORT_ANOTHER}
          </Button>
          <Button type="primary" onClick={handleClose}>
            {IMPORT.RESULT.CLOSE}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      width="80rem"
      centered
      footer={getFooter()}
      title={IMPORT.MODAL_TITLE}
      destroyOnClose
    >
      {step === STEPS.UPLOAD && renderUpload()}
      {step === STEPS.PREVIEW && renderPreview()}
      {step === STEPS.IMPORTING && renderImporting()}
      {step === STEPS.RESULT && renderResult()}
    </Modal>
  );
}

export default ImportModal;
